import axios, { type InternalAxiosRequestConfig } from 'axios';
import { toast } from 'sonner';
import { formatLandingApiError } from '@/lib/format-landing-api-error';
import { fetchLandingToken } from './landing-token';

declare module 'axios' {
	export interface AxiosRequestConfig {
		suppressToast?: boolean;
	}
}

type CachedToken = {
	token: string;
	expiresAt: number;
};

const REFRESH_BEFORE_MS = 60_000;

let cached: CachedToken | null = null;
let refreshPromise: Promise<string> | null = null;

export function clearLandingTokenCache() {
	cached = null;
	refreshPromise = null;
}

export async function getLandingAccessToken(force = false): Promise<string> {
	const now = Date.now();
	if (!force && cached && cached.expiresAt - REFRESH_BEFORE_MS > now) {
		return cached.token;
	}

	if (refreshPromise) {
		return refreshPromise;
	}

	refreshPromise = (async () => {
		try {
			const { token, expiresIn } = await fetchLandingToken();
			cached = {
				token,
				expiresAt: now + expiresIn * 1000,
			};
			return token;
		} finally {
			refreshPromise = null;
		}
	})();

	return refreshPromise;
}

export const landingsApi = axios.create({
	baseURL: import.meta.env.VITE_LANDINGS_URL,
});

landingsApi.interceptors.request.use(async config => {
	const token = await getLandingAccessToken();
	config.headers.Authorization = `Bearer ${token}`;
	return config;
});

landingsApi.interceptors.response.use(
	response => response,
	async (error: unknown) => {
		if (!axios.isAxiosError(error)) {
			return Promise.reject(error);
		}

		const status = error.response?.status;
		const config = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;
		const message = formatLandingApiError(error.response?.data, status);

		if (status === 401 && config && !config._retry) {
			config._retry = true;
			clearLandingTokenCache();
			try {
				const token = await getLandingAccessToken(true);
				config.headers.Authorization = `Bearer ${token}`;
				return landingsApi(config);
			} catch {
				if (!config.suppressToast) {
					toast.error('Сессия лендингов истекла. Войдите снова.');
				}
				return Promise.reject(error);
			}
		}

		if (!config?.suppressToast) {
			if (status === 403) {
				toast.error(message || 'Лендинги отключены');
			} else {
				toast.error(message);
			}
		}

		return Promise.reject(error);
	},
);
